import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { publicAPI, privateAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import PostnummerFilter from '../components/PostnummerFilter'
import ExportButtons from '../components/ExportButtons'
import DateFilterBar, { OutputMode, getDateRangeForMode, formatNorwegianDate } from '../components/DateFilterBar'
import { useYearDates } from '../contexts/YearContext'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
// import PeriodInfoBanner from '../components/PeriodInfoBanner'
import QueryExplanationBox, { buildExplanationText } from '../components/QueryExplanationBox'
import { useMaxDataDate } from '../hooks/useMaxDataDate'
import LeasingWeeklyChart from '../components/LeasingWeeklyChart'
import SearchBar from '../components/SearchBar'
import { Car, PieChart, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import { Pie } from 'react-chartjs-2'
import { ColumnDefinition } from '../utils/exportToCsv'
import { usePersistedState } from '../hooks/usePersistedState'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import { useMineMerker } from '../hooks/useMineMerker'
import { formatLeasingselskap } from '../utils/formatLeasingselskap'
import { localizeApiDetail } from '../i18n/apiError'

// Helper function to safely parse numeric values that might be strings
const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) return 0
  return typeof value === 'string' ? parseFloat(value) : value
}

const formatDecimal = (value: number | string | null | undefined, decimals: number = 1): string => {
  const num = toNumber(value)
  return num.toLocaleString('nb-NO', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

interface MasterModelLeasingData {
  brand: string
  master_model: string
  total_registrations: number
  leasing_count: number | string
  leasing_percentage: number | string
  private_leasing_count: number | string
  private_leasing_percentage: number | string
  company_leasing_count: number | string
  company_leasing_percentage: number | string
  undefined_leasing_count: number | string
  undefined_leasing_percentage: number | string
  ev_count: number | string
  ev_percentage: number | string
  market_share: number | string | null
}

interface LeasingCompanyStats {
  org_number: string
  leasingselskap: string
  total_leases: number
  market_share: number | string
  top_brands: Array<{ brand: string; count: number }>
}

interface BrandLeasingData {
  brand: string
  master_model?: string
  total_count?: number
  total_registrations?: number
  leasing_count: number | string
  leasing_percentage: number | string
  private_leasing_count: number | string
  private_leasing_percentage: number | string
  company_leasing_count: number | string
  company_leasing_percentage: number | string
  undefined_leasing_count: number | string
  undefined_leasing_percentage: number | string
  ev_count: number | string
  ev_percentage: number | string
  market_share: number | string | null
}

export default function LeasingAnalytics() {
  const { t, i18n } = useTranslation('leasingLoans')
  // Period label for the explanation box; mirrors DateFilterBar's OUTPUT_MODE_LABELS.
  // The persisted mode can be one DateFilterBar doesn't know (e.g. last_week from Aldersanalyse).
  const periodLabel = (mode: OutputMode) =>
    i18n.exists(`leasingLoans:outputModes.${mode}`) ? t(`outputModes.${mode}` as const) : undefined
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'brands' | 'models' | 'leasingselskap'>('brands')
  const [leasingStats, setLeasingStats] = useState<any>(null)
  const [weeklyData, setWeeklyData] = useState<any[]>([])
  const [prevYearWeeklyData, setPrevYearWeeklyData] = useState<any[]>([])
  const [masterModelData, setMasterModelData] = useState<MasterModelLeasingData[]>([])
  const [brandData, setBrandData] = useState<BrandLeasingData[]>([])
  const [modelsLoading, setModelsLoading] = useState(false)
  const [brandsLoading, setBrandsLoading] = useState(false)
  const [leasingCompanies, setLeasingCompanies] = useState<LeasingCompanyStats[]>([])
  const [leasingCompaniesLoading, setLeasingCompaniesLoading] = useState(false)

  // Brand filter state (Leasingselskaper tab)
  const [selectedBrands, setSelectedBrands] = useState<string[]>([])
  const [availableBrands, setAvailableBrands] = useState<string[]>([])

  // Search state
  const [brandSearchTerm, setBrandSearchTerm] = useState('')
  const [modelSearchTerm, setModelSearchTerm] = useState('')

  // Models pagination (client-side)
  const MODELS_PAGE_SIZE = 50
  const [modelsPage, setModelsPage] = useState(1)

  // Sort state
  const [brandSortConfig, setBrandSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [modelSortConfig, setModelSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  // Date range state from year context
  const { startDate, setStartDate, endDate, setEndDate, selectedYear, maxSvvDate } = useYearDates('leasing-analytics')

  // Vehicle filter
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()

  // Postal code filter state
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('leasing-analytics:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)

  // Mine merker highlighting
  const { activeMerker } = useMineMerker()

  // Date range validation
  const [dateRangeAlert, setDateRangeAlert] = useState<string | null>(null)
  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [customStartDate, setCustomStartDate] = usePersistedState('bp:filter:customStartDate', '')
  const [customEndDate, setCustomEndDate] = usePersistedState('bp:filter:customEndDate', '')
  const maxDataDate = useMaxDataDate()

  // Queried-state tracking — only update when API fetch actually runs
  const [queriedOutputMode, setQueriedOutputMode] = useState<OutputMode>(outputMode)
  const [queriedVehicleFilter, setQueriedVehicleFilter] = useState(vehicleFilter)
  const [queriedPostnummer, setQueriedPostnummer] = useState(minePostnummer)
  const [queriedStartDate, setQueriedStartDate] = useState(startDate.toISOString().split('T')[0])
  const [queriedEndDate, setQueriedEndDate] = useState(endDate.toISOString().split('T')[0])

  /** Snapshot queried state — call at the start of every fetch */
  const snapshotQueriedState = (overrideStart?: Date, overrideEnd?: Date) => {
    setQueriedOutputMode(outputMode)
    setQueriedVehicleFilter(vehicleFilter)
    setQueriedPostnummer(minePostnummer)
    setQueriedStartDate((overrideStart ?? startDate).toISOString().split('T')[0])
    setQueriedEndDate((overrideEnd ?? endDate).toISOString().split('T')[0])
  }

  useEffect(() => {
    // Reset sort state when switching tabs
    setBrandSortConfig(null)
    setModelSortConfig(null)

    if (activeTab === 'overview') {
      fetchLeasingData()
    } else if (activeTab === 'brands') {
      fetchBrandData()
    } else if (activeTab === 'models') {
      fetchMasterModelData()
    } else if (activeTab === 'leasingselskap') {
      fetchLeasingCompanyData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedYear, vehicleFilter, maxSvvDate, selectedBrands])

  useEffect(() => {
    fetchAvailableBrands()
     
  }, [])

  // Validate date range (max 366 days)
  const validateDateRange = (start: Date, end: Date): boolean => {
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays > 366) {
      setDateRangeAlert(t('leasing.dateRangeTooLarge', { days: diffDays }))
      return false
    }
    
    setDateRangeAlert(null)
    return true
  }

  const handleOutputModeChange = (mode: OutputMode) => {
    setOutputMode(mode)
    // For non-custom modes, auto-fetch with the new date range
    if (mode !== 'custom') {
      const range = getDateRangeForMode(mode, maxSvvDate)
      const s = new Date(range.startDate)
      const e = new Date(range.endDate)
      setStartDate(s)
      setEndDate(e)
      if (activeTab === 'overview') fetchLeasingData(s, e)
      else if (activeTab === 'brands') fetchBrandData(s, e)
      else if (activeTab === 'models') fetchMasterModelData(s, e)
      else if (activeTab === 'leasingselskap') fetchLeasingCompanyData(s, e)
      // The fetch above snapshots outputMode from this render's closure, i.e. the
      // mode before the click. Overwrite it with the new one (#713).
      setQueriedOutputMode(mode)
    }
  }

  const handleApplyFilters = () => {
    let s: Date, e: Date
    if (outputMode === 'custom') {
      if (!customStartDate || !customEndDate) return
      s = new Date(customStartDate)
      e = new Date(customEndDate)
      if (!validateDateRange(s, e)) return
    } else {
      const range = getDateRangeForMode(outputMode, maxSvvDate)
      s = new Date(range.startDate)
      e = new Date(range.endDate)
      setDateRangeAlert(null)
    }
    setStartDate(s)
    setEndDate(e)
    if (activeTab === 'overview') fetchLeasingData(s, e)
    else if (activeTab === 'brands') fetchBrandData(s, e)
    else if (activeTab === 'models') fetchMasterModelData(s, e)
    else if (activeTab === 'leasingselskap') fetchLeasingCompanyData(s, e)
  }


  // Sort handler
  const handleSort = (key: string, table: 'brands' | 'models') => {
    const currentConfig = table === 'brands' ? brandSortConfig : modelSortConfig
    const setConfig = table === 'brands' ? setBrandSortConfig : setModelSortConfig

    if (currentConfig?.key === key) {
      // Toggle: asc -> desc -> null (clear)
      if (currentConfig.direction === 'asc') {
        setConfig({ key, direction: 'desc' })
      } else {
        setConfig(null)
      }
    } else {
      // New column, start with ascending
      setConfig({ key, direction: 'asc' })
    }
  }

  // Get sort icon for column header
  const getSortIcon = (columnKey: string, table: 'brands' | 'models') => {
    const config = table === 'brands' ? brandSortConfig : modelSortConfig
    if (config?.key === columnKey) {
      return config.direction === 'asc' ? 
        <ArrowUp className="w-4 h-4 inline ml-1" /> : 
        <ArrowDown className="w-4 h-4 inline ml-1" />
    }
    return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  }

  // Sorted brand data
  const sortedBrandData = useMemo(() => {
    if (!brandSortConfig) return brandData

    const totalRow = brandData.find(b => b.brand === 'TOTAL' || b.master_model === 'ALL MODELS' || b.market_share === null)
    const dataRows = brandData.filter(b => b.brand !== 'TOTAL' && b.master_model !== 'ALL MODELS' && b.market_share !== null)

    const sorted = [...dataRows].sort((a, b) => {
      let aVal: any
      let bVal: any

      switch (brandSortConfig.key) {
        case 'brand':
          aVal = a.brand.toLowerCase()
          bVal = b.brand.toLowerCase()
          break
        case 'total_registrations':
          aVal = toNumber(a.total_count || a.total_registrations)
          bVal = toNumber(b.total_count || b.total_registrations)
          break
        case 'market_share':
          aVal = a.market_share !== null ? toNumber(a.market_share) : -Infinity
          bVal = b.market_share !== null ? toNumber(b.market_share) : -Infinity
          break
        case 'leasing_count':
          aVal = toNumber(a.leasing_count)
          bVal = toNumber(b.leasing_count)
          break
        case 'leasing_percentage':
          aVal = toNumber(a.leasing_percentage)
          bVal = toNumber(b.leasing_percentage)
          break
        case 'private_leasing_percentage':
          aVal = toNumber(a.private_leasing_percentage)
          bVal = toNumber(b.private_leasing_percentage)
          break
        case 'company_leasing_percentage':
          aVal = toNumber(a.company_leasing_percentage)
          bVal = toNumber(b.company_leasing_percentage)
          break
        case 'undefined_leasing_percentage':
          aVal = toNumber(a.undefined_leasing_percentage)
          bVal = toNumber(b.undefined_leasing_percentage)
          break
        default:
          return 0
      }

      if (typeof aVal === 'string') {
        return brandSortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return brandSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
    })

    return totalRow ? [totalRow, ...sorted] : sorted
  }, [brandData, brandSortConfig])

  // Sorted model data
  const sortedModelData = useMemo(() => {
    if (!modelSortConfig) return masterModelData

    const totalRow = masterModelData.find(m => m.brand === 'TOTAL' || m.master_model === 'ALL MODELS' || m.market_share === null)
    const dataRows = masterModelData.filter(m => m.brand !== 'TOTAL' && m.master_model !== 'ALL MODELS' && m.market_share !== null)

    const sorted = [...dataRows].sort((a, b) => {
      let aVal: any
      let bVal: any

      switch (modelSortConfig.key) {
        case 'brand':
          aVal = a.brand.toLowerCase()
          bVal = b.brand.toLowerCase()
          break
        case 'master_model':
          aVal = a.master_model.toLowerCase()
          bVal = b.master_model.toLowerCase()
          break
        case 'total_registrations':
          aVal = toNumber(a.total_registrations)
          bVal = toNumber(b.total_registrations)
          break
        case 'market_share':
          aVal = a.market_share !== null ? toNumber(a.market_share) : -Infinity
          bVal = b.market_share !== null ? toNumber(b.market_share) : -Infinity
          break
        case 'leasing_count':
          aVal = toNumber(a.leasing_count)
          bVal = toNumber(b.leasing_count)
          break
        case 'leasing_percentage':
          aVal = toNumber(a.leasing_percentage)
          bVal = toNumber(b.leasing_percentage)
          break
        case 'private_leasing_percentage':
          aVal = toNumber(a.private_leasing_percentage)
          bVal = toNumber(b.private_leasing_percentage)
          break
        case 'company_leasing_percentage':
          aVal = toNumber(a.company_leasing_percentage)
          bVal = toNumber(b.company_leasing_percentage)
          break
        case 'undefined_leasing_percentage':
          aVal = toNumber(a.undefined_leasing_percentage)
          bVal = toNumber(b.undefined_leasing_percentage)
          break
        default:
          return 0
      }

      if (typeof aVal === 'string') {
        return modelSortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return modelSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
    })

    return totalRow ? [totalRow, ...sorted] : sorted
  }, [masterModelData, modelSortConfig])

  // Static column label for registration count
  const getPeriodColumnLabel = (): string => t('leasing.cols.cars')

  // Filtered brand data by search
  const filteredBrandData = useMemo(() => {
    if (!brandSearchTerm) return sortedBrandData
    const term = brandSearchTerm.toLowerCase()
    return sortedBrandData.filter(b =>
      b.brand === 'TOTAL' || b.brand.toLowerCase().includes(term)
    )
  }, [sortedBrandData, brandSearchTerm])

  // Filtered model data by search
  const filteredModelData = useMemo(() => {
    if (!modelSearchTerm) return sortedModelData
    const term = modelSearchTerm.toLowerCase()
    return sortedModelData.filter(m =>
      m.brand === 'TOTAL' || m.brand.toLowerCase().includes(term) || m.master_model.toLowerCase().includes(term)
    )
  }, [sortedModelData, modelSearchTerm])

  // Reset models pagination when underlying filtered list changes
  useEffect(() => {
    setModelsPage(1)
  }, [modelSearchTerm, modelSortConfig, masterModelData])

  // Paginated slice for Modeller tab — keep TOTAL row pinned at top of page 1
  const { paginatedModelData, modelsTotalRows, modelsTotalPages } = useMemo(() => {
    const totalRow = filteredModelData.find(
      m => m.brand === 'TOTAL' || m.master_model === 'ALL MODELS' || m.market_share === null
    )
    const dataRows = filteredModelData.filter(
      m => m.brand !== 'TOTAL' && m.master_model !== 'ALL MODELS' && m.market_share !== null
    )
    const totalPages = Math.max(1, Math.ceil(dataRows.length / MODELS_PAGE_SIZE))
    const safePage = Math.min(Math.max(1, modelsPage), totalPages)
    const startIdx = (safePage - 1) * MODELS_PAGE_SIZE
    const pageRows = dataRows.slice(startIdx, startIdx + MODELS_PAGE_SIZE)
    const withTotal = totalRow && safePage === 1 ? [totalRow, ...pageRows] : pageRows
    return {
      paginatedModelData: withTotal,
      modelsTotalRows: dataRows.length,
      modelsTotalPages: totalPages,
    }
  }, [filteredModelData, modelsPage])

  // Column definitions for export
  const brandColumns: ColumnDefinition[] = [
    { key: 'brand', label: t('leasing.export.brand') },
    {
      key: 'total_registrations',
      label: t('leasing.export.cars'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    {
      key: 'leasing_count',
      label: t('leasing.export.leasing'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    {
      key: 'leasing_percentage',
      label: t('leasing.export.leasingShare'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
    },
    {
      key: 'private_leasing_percentage',
      label: t('leasing.export.privateLeasing'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
    },
    {
      key: 'company_leasing_percentage',
      label: t('leasing.export.companyLeasing'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
    },
    {
      key: 'undefined_leasing_percentage',
      label: t('leasing.export.undefinedLeasing'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
    },
    {
      key: 'market_share',
      label: t('leasing.export.marketShare'),
      format: (val) => val !== null ? toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2}) : '-'
    },
  ]

  const modelColumns: ColumnDefinition[] = [
    { key: 'brand', label: t('leasing.export.brand') },
    { key: 'master_model', label: t('leasing.export.model') },
    {
      key: 'total_registrations',
      label: t('leasing.export.cars'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    {
      key: 'leasing_count',
      label: t('leasing.export.leasing'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    {
      key: 'leasing_percentage',
      label: t('leasing.export.leasingShare'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
    },
    {
      key: 'private_leasing_percentage',
      label: t('leasing.export.privateLeasing'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
    },
    {
      key: 'company_leasing_percentage',
      label: t('leasing.export.companyLeasing'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
    },
    {
      key: 'undefined_leasing_percentage',
      label: t('leasing.export.undefinedLeasing'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
    },
    {
      key: 'market_share',
      label: t('leasing.export.marketShare'),
      format: (val) => val !== null ? toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2}) : '-'
    },
  ]

  const fetchLeasingData = async (overrideStart?: Date, overrideEnd?: Date) => {
    try {
      setLoading(true)
      setError(null)
      snapshotQueriedState(overrideStart, overrideEnd)

      const start_date = (overrideStart ?? startDate).toISOString().split('T')[0]
      const end_date = (overrideEnd ?? endDate).toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date }

      // Add vehicle filter
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }

      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      const varebilWeeklyParam = getVarebilParam()
      const weeklyParams: any = { year: parseInt(selectedYear) }
      if (varebilWeeklyParam !== undefined) weeklyParams.varebil = varebilWeeklyParam

      const [leasingRes, weeklyRes] = await Promise.all([
        publicAPI.getLeasingStatistics(params),
        publicAPI.getWeeklyTimeSeries(weeklyParams),
      ])

      setLeasingStats(leasingRes.data.data)
      setWeeklyData(weeklyRes.data.data || [])
      setPrevYearWeeklyData(weeklyRes.data.prev_year_data || [])
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('leasing.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const fetchBrandData = async (overrideStart?: Date, overrideEnd?: Date) => {
    try {
      setBrandsLoading(true)
      setError(null)
      snapshotQueriedState(overrideStart, overrideEnd)

      const start_date = (overrideStart ?? startDate).toISOString().split('T')[0]
      const end_date = (overrideEnd ?? endDate).toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date }
      // No limit - fetch all brands with leasing > 0

      // Add vehicle filter
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }

      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      // Fetch both brand data and dashboard stats in parallel
      const [brandResponse, statsResponse] = await Promise.all([
        privateAPI.getLeasingByBrand(params),
        publicAPI.getLeasingStatistics(params),
      ])

      const brands = brandResponse.data.data || []
      const stats = statsResponse.data.data

      // Replace TOTAL row with data from dashboard API
      const filteredBrands = brands.filter((b: any) => 
        b.brand !== 'TOTAL' && b.master_model !== 'ALL MODELS' && b.market_share !== null
      )

      // Create TOTAL row from dashboard stats
      const totalRow: BrandLeasingData = {
        brand: 'TOTAL',
        master_model: 'ALL MODELS',
        total_registrations: stats.total_registrations,
        leasing_count: (stats.org_leasing_count || 0) + (stats.private_leasing_count || 0) + (stats.undefined_leasing_count || 0),
        leasing_percentage: stats.total_registrations > 0 ? (((stats.org_leasing_count || 0) + (stats.private_leasing_count || 0) + (stats.undefined_leasing_count || 0)) / stats.total_registrations * 100) : 0,
        private_leasing_count: stats.private_leasing_count || 0,
        private_leasing_percentage: stats.private_leasing_share || 0,
        company_leasing_count: stats.org_leasing_count || 0,
        company_leasing_percentage: stats.org_leasing_share || 0,
        undefined_leasing_count: stats.undefined_leasing_count || 0,
        undefined_leasing_percentage: stats.undefined_leasing_share || 0,
        ev_count: 0,
        ev_percentage: 0,
        market_share: null,
      }

      setBrandData([totalRow, ...filteredBrands])
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('leasing.loadBrandsError'))
    } finally {
      setBrandsLoading(false)
    }
  }

  const fetchMasterModelData = async (overrideStart?: Date, overrideEnd?: Date) => {
    try {
      setModelsLoading(true)
      setError(null)
      snapshotQueriedState(overrideStart, overrideEnd)

      const start_date = (overrideStart ?? startDate).toISOString().split('T')[0]
      const end_date = (overrideEnd ?? endDate).toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date }
      // No limit - fetch all models

      // Add vehicle filter
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }

      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      // Fetch both model data and dashboard stats in parallel
      const [modelResponse, statsResponse] = await Promise.all([
        privateAPI.getLeasingByMasterModel(params),
        publicAPI.getLeasingStatistics(params),
      ])

      const models = modelResponse.data.data || []
      const stats = statsResponse.data.data

      // Replace TOTAL row with data from dashboard API
      const filteredModels = models.filter((m: any) => 
        m.brand !== 'TOTAL' && m.master_model !== 'ALL MODELS' && m.market_share !== null
      )

      // Create TOTAL row from dashboard stats
      const totalRow: MasterModelLeasingData = {
        brand: 'TOTAL',
        master_model: 'ALL MODELS',
        total_registrations: stats.total_registrations,
        leasing_count: (stats.org_leasing_count || 0) + (stats.private_leasing_count || 0) + (stats.undefined_leasing_count || 0),
        leasing_percentage: stats.total_registrations > 0 ? (((stats.org_leasing_count || 0) + (stats.private_leasing_count || 0) + (stats.undefined_leasing_count || 0)) / stats.total_registrations * 100) : 0,
        private_leasing_count: stats.private_leasing_count || 0,
        private_leasing_percentage: stats.private_leasing_share || 0,
        company_leasing_count: stats.org_leasing_count || 0,
        company_leasing_percentage: stats.org_leasing_share || 0,
        undefined_leasing_count: stats.undefined_leasing_count || 0,
        undefined_leasing_percentage: stats.undefined_leasing_share || 0,
        ev_count: 0,
        ev_percentage: 0,
        market_share: null,
      }

      setMasterModelData([totalRow, ...filteredModels])
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('leasing.loadModelsError'))
    } finally {
      setModelsLoading(false)
    }
  }

  const fetchAvailableBrands = async () => {
    try {
      const response = await publicAPI.getBrands()
      const brandNames: string[] = response.data?.brands ?? []
      if (brandNames.length === 0) {
        console.warn('getBrands returned no brand names; using fallback')
        setAvailableBrands(['Tesla', 'BMW', 'Audi', 'Mercedes-Benz', 'Volkswagen', 'Toyota', 'Volvo'])
      } else {
        setAvailableBrands(brandNames)
      }
    } catch (err) {
      console.error('Failed to fetch brands:', err)
      setAvailableBrands(['Tesla', 'BMW', 'Audi', 'Mercedes-Benz', 'Volkswagen', 'Toyota', 'Volvo'])
    }
  }

  const fetchLeasingCompanyData = async (overrideStart?: Date, overrideEnd?: Date) => {
    setLeasingCompaniesLoading(true)
    setError(null)
    snapshotQueriedState(overrideStart, overrideEnd)
    try {
      const start_date = (overrideStart ?? startDate).toISOString().split('T')[0]
      const end_date = (overrideEnd ?? endDate).toISOString().split('T')[0]
      const params: {
        start_date: string; end_date: string; limit: number;
        varebil?: boolean; MerkeNavn?: string; mine_postnummer?: string;
      } = { start_date, end_date, limit: 20 }

      const varebilParam = getVarebilParam()
      if (varebilParam !== undefined) params.varebil = varebilParam
      if (minePostnummer) params.mine_postnummer = minePostnummer
      if (selectedBrands.length > 0) params.MerkeNavn = selectedBrands.join(',')

      const res = await publicAPI.getLeasingByLeasingselskap(params)
      setLeasingCompanies(res.data.data || [])
    } catch (err: any) {
      setError(localizeApiDetail(err?.response?.data?.detail) || err?.message || t('leasing.loadCompaniesError'))
      setLeasingCompanies([])
    } finally {
      setLeasingCompaniesLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('leasing.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('leasing.subtitle')}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('leasing.source')}</p>
      </div>

      {/* Date Range Filter */}
      <DateFilterBar
        outputMode={outputMode}
        onOutputModeChange={handleOutputModeChange}
        customStartDate={customStartDate}
        onCustomStartDateChange={setCustomStartDate}
        customEndDate={customEndDate}
        onCustomEndDateChange={setCustomEndDate}
        onApply={handleApplyFilters}
        bottomContent={activeTab !== 'leasingselskap' ? (
          <PostnummerFilter
            enabled={postnummerFilterEnabled}
            onToggle={setPostnummerFilterEnabled}
            compact
          />
        ) : undefined}
      >
        {activeTab === 'leasingselskap' && (
          <div className="min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('leasing.brandFilterLabel')}
            </label>
            <select
              value={selectedBrands[0] || ''}
              onChange={(e) => {
                setSelectedBrands(e.target.value ? [e.target.value] : [])
              }}
              className="input-field"
            >
              <option value="">{t('leasing.allBrands')}</option>
              {availableBrands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>
        )}
      </DateFilterBar>

      {/* Date Range Alert */}
      {dateRangeAlert && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">
            <strong>{t('leasing.errorPrefix')}</strong> {dateRangeAlert}
          </p>
        </div>
      )}

      {/* Period Info Banner */}
      {/* <PeriodInfoBanner
        maxDataDate={maxDataDate}
        outputMode={outputMode}
        periodLabel={OUTPUT_MODE_LABELS[outputMode]}
        startDate={startDate.toISOString().split('T')[0]}
        endDate={endDate.toISOString().split('T')[0]}
      /> */}

      {/* Query Explanation Box */}
      <QueryExplanationBox
        maxDataDate={maxDataDate}
        outputMode={queriedOutputMode}
        vehicleFilter={queriedVehicleFilter}
        postnummerFilter={queriedPostnummer}
        periodLabel={periodLabel(queriedOutputMode)}
        startDate={queriedStartDate}
        endDate={queriedEndDate}
      />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-dark-border">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'overview'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <PieChart className="w-4 h-4" />
            {t('leasing.tabs.overview')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('brands')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'brands'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Car className="w-4 h-4" />
            {t('leasing.tabs.brands')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('models')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'models'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Car className="w-4 h-4" />
            {t('leasing.tabs.models')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('leasingselskap')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'leasingselskap'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <PieChart className="w-4 h-4" />
            {t('leasing.tabs.leasingselskap')}
          </span>
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {error && (
            <ErrorMessage message={error} />
          )}
          {loading ? (
            <div className="card p-8 text-center">
              <LoadingSpinner />
            </div>
          ) : !leasingStats ? (
            <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
              {t('leasing.noDataApply')}
            </div>
          ) : (
            <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="card p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('leasing.kpi.totalRegistrations')}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {leasingStats?.total_registrations?.toLocaleString('nb-NO')}
          </p>
        </div>
        <div className="card p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('leasing.kpi.notLeasing')}</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {leasingStats && leasingStats.total_registrations > 0 ? (
              ((leasingStats.total_registrations -
                leasingStats.org_leasing_count -
                leasingStats.private_leasing_count -
                (leasingStats.undefined_leasing_count || 0)) /
                leasingStats.total_registrations * 100
              ).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
            ) : '0,00'} %
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {leasingStats ? (
              leasingStats.total_registrations -
              leasingStats.org_leasing_count -
              leasingStats.private_leasing_count -
              (leasingStats.undefined_leasing_count || 0)
            ).toLocaleString('nb-NO') : '–'}
          </p>
        </div>
        <div className="card p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('leasing.kpi.companyLeasing')}</p>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {leasingStats?.org_leasing_share?.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('leasing.kpi.cars', { n: leasingStats?.org_leasing_count?.toLocaleString('nb-NO') ?? '' })}
          </p>
        </div>
        <div className="card p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('leasing.kpi.privateLeasing')}</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {leasingStats?.private_leasing_share?.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('leasing.kpi.cars', { n: leasingStats?.private_leasing_count?.toLocaleString('nb-NO') ?? '' })}
          </p>
        </div>
        <div className="card p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('leasing.kpi.undefinedLeasing')}</p>
          <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">
            {(leasingStats?.undefined_leasing_share || 0).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('leasing.kpi.cars', { n: (leasingStats?.undefined_leasing_count || 0).toLocaleString('nb-NO') })}
          </p>
        </div>
      </div>

      {/* Weekly Leasing Chart */}
      {weeklyData.length > 0 && (
        <LeasingWeeklyChart data={weeklyData} prevYearData={prevYearWeeklyData} year={parseInt(selectedYear)} />
      )}
            </>
          )}
        </>
      )}

      {/* Brands Tab */}
      {activeTab === 'brands' && (
        <>
          {error && (
            <ErrorMessage message={error} />
          )}
          {brandsLoading ? (
            <div className="card p-8 text-center">
              <LoadingSpinner />
            </div>
          ) : brandData.length === 0 ? (
            <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
              {t('leasing.noDataApply')}
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-4">
                <div>
                  {brandSortConfig && (
                    <button 
                      onClick={() => setBrandSortConfig(null)}
                      className="btn-secondary text-sm"
                    >
                      {t('leasing.resetSort')}
                    </button>
                  )}
                </div>
                <ExportButtons
                  data={sortedBrandData}
                  columns={brandColumns}
                  metadata={{
                    title: t('leasing.export.titleBrands'),
                    dateRange: t('leasing.export.dateRange', { start: formatNorwegianDate(startDate), end: formatNorwegianDate(endDate) }),
                    filters: [
                      vehicleFilter === 'personbil' ? t('leasing.export.filterPersonbil') : 
                      vehicleFilter === 'varebil' ? t('leasing.export.filterVarebil') : '',
                      minePostnummer ? t('leasing.export.filterPostnummer', { value: minePostnummer }) : ''
                    ].filter(f => f),
                    explanationText: buildExplanationText({
                      maxDataDate, outputMode: queriedOutputMode, vehicleFilter: queriedVehicleFilter,
                      postnummerFilter: queriedPostnummer, periodLabel: periodLabel(queriedOutputMode),
                      startDate: queriedStartDate, endDate: queriedEndDate,
                    }),
                  }}
                  disabled={brandsLoading}
                />
              </div>
              <div className="mb-4">
                <SearchBar value={brandSearchTerm} onChange={setBrandSearchTerm} placeholder={t('leasing.searchBrand')} />
              </div>
              <div className="card overflow-x-auto">
                <table className="data-table data-table-compact">
                  <thead>
                    <tr>
                      <th className="text-center">{t('leasing.cols.rank')}</th>
                      <th className="text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('brand', 'brands')}>
                        {t('leasing.cols.brand')} {getSortIcon('brand', 'brands')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('total_registrations', 'brands')}>
                        {getPeriodColumnLabel()} {getSortIcon('total_registrations', 'brands')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('leasing_count', 'brands')}>
                        {t('leasing.cols.leasing')} {getSortIcon('leasing_count', 'brands')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border-r-2 border-gray-300 dark:border-gray-600" onClick={() => handleSort('leasing_percentage', 'brands')}>
                        {t('leasing.cols.leasingShare')} {getSortIcon('leasing_percentage', 'brands')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('private_leasing_percentage', 'brands')}>
                        {t('leasing.cols.privateLeasing')} {getSortIcon('private_leasing_percentage', 'brands')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('company_leasing_percentage', 'brands')}>
                        {t('leasing.cols.companyLeasing')} {getSortIcon('company_leasing_percentage', 'brands')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border-r-2 border-gray-300 dark:border-gray-600" onClick={() => handleSort('undefined_leasing_percentage', 'brands')}>
                        {t('leasing.cols.undefinedLeasing')} {getSortIcon('undefined_leasing_percentage', 'brands')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('market_share', 'brands')}>
                        {t('leasing.cols.marketShare')} {getSortIcon('market_share', 'brands')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBrandData.map((brand, index) => {
                      const isTotal = brand.brand === 'TOTAL' || brand.master_model === 'ALL MODELS' || brand.market_share === null
                      return (
                        <tr
                          key={`${brand.brand}-${index}`}
                          className={isTotal ? 'bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600' : `${activeMerker.has(brand.brand) ? 'bg-primary-100 dark:bg-primary-900/30 border-l-4 border-primary-500' : index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
                        >
                          <td className="text-center">
                            {isTotal ? (
                              <span className="text-primary-600 dark:text-primary-400 font-bold">∑</span>
                            ) : (
                              <span className="text-gray-700 dark:text-gray-300">{index}</span>
                            )}
                          </td>
                          <td className={isTotal ? 'font-bold text-primary-700 dark:text-primary-300' : 'font-semibold'}>{brand.brand}</td>
                          <td className="text-right">{(brand.total_count || brand.total_registrations || 0).toLocaleString('nb-NO')}</td>
                          <td className="text-right">{toNumber(brand.leasing_count).toLocaleString('nb-NO')}</td>
                          <td className="text-right border-r-2 border-gray-300 dark:border-gray-600">
                            {toNumber(brand.leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}{isTotal ? ' %' : ''}
                          </td>
                          <td className="text-right">
                            {toNumber(brand.private_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}{isTotal ? ' %' : ''}
                            <span className="text-xs text-gray-500 ml-1">
                              ({toNumber(brand.private_leasing_count).toLocaleString('nb-NO')})
                            </span>
                          </td>
                          <td className="text-right">
                            {toNumber(brand.company_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}{isTotal ? ' %' : ''}
                            <span className="text-xs text-gray-500 ml-1">
                              ({toNumber(brand.company_leasing_count).toLocaleString('nb-NO')})
                            </span>
                          </td>
                          <td className="text-right border-r-2 border-gray-300 dark:border-gray-600">
                            {toNumber(brand.undefined_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}{isTotal ? ' %' : ''}
                            <span className="text-xs text-gray-500 ml-1">
                              ({toNumber(brand.undefined_leasing_count).toLocaleString('nb-NO')})
                            </span>
                          </td>
                          <td className="text-right">
                            {brand.market_share !== null ? (
                              <span>
                                {toNumber(brand.market_share).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}{isTotal ? ' %' : ''}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* Models Tab */}
      {activeTab === 'models' && (
        <>
          {error && (
            <ErrorMessage message={error} />
          )}
          {modelsLoading ? (
            <div className="card p-8 text-center">
              <LoadingSpinner />
            </div>
          ) : masterModelData.length === 0 ? (
            <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
              {t('leasing.noDataApply')}
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-4">
                <div>
                  {modelSortConfig && (
                    <button 
                      onClick={() => setModelSortConfig(null)}
                      className="btn-secondary text-sm"
                    >
                      {t('leasing.resetSort')}
                    </button>
                  )}
                </div>
                <ExportButtons
                  data={sortedModelData}
                  columns={modelColumns}
                  metadata={{
                    title: t('leasing.export.titleModels'),
                    dateRange: t('leasing.export.dateRange', { start: formatNorwegianDate(startDate), end: formatNorwegianDate(endDate) }),
                    filters: [
                      vehicleFilter === 'personbil' ? t('leasing.export.filterPersonbil') : 
                      vehicleFilter === 'varebil' ? t('leasing.export.filterVarebil') : '',
                      minePostnummer ? t('leasing.export.filterPostnummer', { value: minePostnummer }) : ''
                    ].filter(f => f),
                    explanationText: buildExplanationText({
                      maxDataDate, outputMode: queriedOutputMode, vehicleFilter: queriedVehicleFilter,
                      postnummerFilter: queriedPostnummer, periodLabel: periodLabel(queriedOutputMode),
                      startDate: queriedStartDate, endDate: queriedEndDate,
                    }),
                  }}
                  disabled={modelsLoading}
                />
              </div>
              <div className="mb-4">
                <SearchBar value={modelSearchTerm} onChange={setModelSearchTerm} placeholder={t('leasing.searchBrandModel')} />
              </div>
              <div className="card overflow-x-auto">
                <table className="data-table data-table-compact">
                  <thead>
                    <tr>
                      <th className="text-center">{t('leasing.cols.rank')}</th>
                      <th className="text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('brand', 'models')}>
                        {t('leasing.cols.brand')} {getSortIcon('brand', 'models')}
                      </th>
                      <th className="text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('master_model', 'models')}>
                        {t('leasing.cols.model')} {getSortIcon('master_model', 'models')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('total_registrations', 'models')}>
                        {getPeriodColumnLabel()} {getSortIcon('total_registrations', 'models')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('leasing_count', 'models')}>
                        {t('leasing.cols.leasing')} {getSortIcon('leasing_count', 'models')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border-r-2 border-gray-300 dark:border-gray-600" onClick={() => handleSort('leasing_percentage', 'models')}>
                        {t('leasing.cols.leasingShare')} {getSortIcon('leasing_percentage', 'models')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('private_leasing_percentage', 'models')}>
                        {t('leasing.cols.privateLeasing')} {getSortIcon('private_leasing_percentage', 'models')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('company_leasing_percentage', 'models')}>
                        {t('leasing.cols.companyLeasing')} {getSortIcon('company_leasing_percentage', 'models')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border-r-2 border-gray-300 dark:border-gray-600" onClick={() => handleSort('undefined_leasing_percentage', 'models')}>
                        {t('leasing.cols.undefinedLeasing')} {getSortIcon('undefined_leasing_percentage', 'models')}
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('market_share', 'models')}>
                        {t('leasing.cols.marketShare')} {getSortIcon('market_share', 'models')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedModelData.map((model, index) => {
                    const isTotal = model.brand === 'TOTAL' || model.master_model === 'ALL MODELS' || model.market_share === null
                    // Global rank across all pages (TOTAL row pinned to page 1 only)
                    const totalOffset = isTotal ? 0 : (modelsPage === 1 ? -1 : 0)
                    const rank = isTotal ? 0 : (modelsPage - 1) * MODELS_PAGE_SIZE + index + 1 + totalOffset
                    return (
                      <tr
                        key={`${model.brand}-${model.master_model}-${index}`}
                        className={isTotal ? 'bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600' : `${index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
                      >
                        <td className="text-center">
                          {isTotal ? (
                            <span className="text-primary-600 dark:text-primary-400 font-bold">∑</span>
                          ) : (
                            <span className="text-gray-700 dark:text-gray-300">{rank}</span>
                          )}
                        </td>
                        <td className={isTotal ? 'font-bold text-primary-700 dark:text-primary-300' : 'font-semibold'}>{model.brand}</td>
                        <td className={isTotal ? 'font-bold text-primary-700 dark:text-primary-300' : 'text-left'}>{isTotal ? '' : model.master_model}</td>
                      <td className="text-right">{model.total_registrations.toLocaleString('nb-NO')}</td>
                      <td className="text-right">{toNumber(model.leasing_count).toLocaleString('nb-NO')}</td>
                      <td className="text-right border-r-2 border-gray-300 dark:border-gray-600">
                        {toNumber(model.leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}{isTotal ? ' %' : ''}
                      </td>
                      <td className="text-right">
                        {toNumber(model.private_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}{isTotal ? ' %' : ''}
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(model.private_leasing_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {toNumber(model.company_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}{isTotal ? ' %' : ''}
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(model.company_leasing_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right border-r-2 border-gray-300 dark:border-gray-600">
                        {toNumber(model.undefined_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}{isTotal ? ' %' : ''}
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(model.undefined_leasing_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {model.market_share !== null ? (
                          <span>
                            {toNumber(model.market_share).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}{isTotal ? ' %' : ''}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {modelsTotalRows > MODELS_PAGE_SIZE && (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('leasing.pagination.showing', { from: (modelsPage - 1) * MODELS_PAGE_SIZE + 1, to: Math.min(modelsPage * MODELS_PAGE_SIZE, modelsTotalRows), total: modelsTotalRows.toLocaleString('nb-NO') })}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setModelsPage(1)}
                    disabled={modelsPage === 1 || modelsLoading}
                    className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('leasing.pagination.first')}
                  </button>
                  <button
                    onClick={() => setModelsPage(p => Math.max(1, p - 1))}
                    disabled={modelsPage === 1 || modelsLoading}
                    className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('leasing.pagination.previous')}
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">
                    {t('leasing.pagination.page', { page: modelsPage, pages: modelsTotalPages })}
                  </span>
                  <button
                    onClick={() => setModelsPage(p => Math.min(modelsTotalPages, p + 1))}
                    disabled={modelsPage === modelsTotalPages || modelsLoading}
                    className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('leasing.pagination.next')}
                  </button>
                  <button
                    onClick={() => setModelsPage(modelsTotalPages)}
                    disabled={modelsPage === modelsTotalPages || modelsLoading}
                    className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('leasing.pagination.last')}
                  </button>
                </div>
              </div>
            )}
            </>          )}
        </>
      )}

      {/* Leasingselskaper Tab */}
      {activeTab === 'leasingselskap' && (
        <>
          {leasingCompaniesLoading ? (
            <div className="card p-8 text-center">
              <LoadingSpinner />
            </div>
          ) : leasingCompanies.length === 0 ? (
            <div className="card p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400 mb-2">{t('leasing.companies.noData')}</p>
              {selectedBrands.length > 0 && (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  {t('leasing.companies.noneForBrands', { brands: selectedBrands.join(', ') })}
                </p>
              )}
            </div>
          ) : (
            <>
              {/* Market Share Pie Chart + Top 5 summary */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="card p-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                    {t('leasing.companies.marketShareTitle')}{selectedBrands.length > 0 ? ` ${selectedBrands.join(', ')}` : ''}
                  </h2>
                  <div className="h-80">
                    <Pie
                      data={{
                        labels: (() => {
                          const top5 = leasingCompanies.slice(0, 5)
                          const labels = top5.map(c => formatLeasingselskap(c.leasingselskap))
                          const top5Sum = top5.reduce((sum, c) => sum + toNumber(c.market_share), 0)
                          const otherShare = 100 - top5Sum
                          if (otherShare > 0) labels.push(t('leasing.companies.other'))
                          return labels
                        })(),
                        datasets: [{
                          data: (() => {
                            const top5 = leasingCompanies.slice(0, 5)
                            const data = top5.map(c => toNumber(c.market_share))
                            const top5Sum = data.reduce((s, v) => s + v, 0)
                            const otherShare = 100 - top5Sum
                            if (otherShare > 0) data.push(otherShare)
                            return data
                          })(),
                          backgroundColor: [
                            'rgba(59, 130, 246, 0.7)',
                            'rgba(16, 185, 129, 0.7)',
                            'rgba(245, 158, 11, 0.7)',
                            'rgba(239, 68, 68, 0.7)',
                            'rgba(168, 85, 247, 0.7)',
                            'rgba(156, 163, 175, 0.7)',
                          ],
                          borderWidth: 2,
                          borderColor: '#fff',
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { position: 'bottom' } },
                      }}
                    />
                  </div>
                </div>

                {/* Top 5 summary card */}
                <div className="card p-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                    {t('leasing.companies.top5Title')}{selectedBrands.length > 0 ? ` ${selectedBrands.join(', ')}` : ''}
                  </h2>
                  <div className="space-y-4">
                    {leasingCompanies.slice(0, 5).map((c, index) => (
                      <div key={c.org_number} className="border-b border-gray-200 dark:border-dark-border pb-3">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                              #{index + 1}
                            </span>
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-gray-100">
                                {formatLeasingselskap(c.leasingselskap)}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {t('leasing.companies.leases', { n: c.total_leases.toLocaleString('nb-NO') })}
                              </p>
                            </div>
                          </div>
                          <span className="text-lg font-bold text-primary-600 dark:text-primary-400">
                            {formatDecimal(c.market_share)}%
                          </span>
                        </div>
                        {/* One selected brand makes the breakdown that brand at 100 % (#406) */}
                        {selectedBrands.length !== 1 && c.top_brands && c.top_brands.length > 0 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {t('leasing.companies.topBrands', { brands: c.top_brands.map(b => b.brand).join(', ') })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Full table */}
              <div className="card overflow-x-auto">
                <table className="data-table data-table-compact">
                  <thead>
                    <tr>
                      <th className="text-center w-12">#</th>
                      <th>{t('leasing.companies.colCompany')}</th>
                      <th className="text-right">{t('leasing.companies.colTotalLeases')}</th>
                      <th className="text-right">{t('leasing.companies.colMarketShare')}</th>
                      {selectedBrands.length !== 1 && <th>{t('leasing.companies.colBrandsShare')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {leasingCompanies.map((c, index) => (
                      <tr key={c.org_number}>
                        <td className="text-center text-gray-700 dark:text-gray-300">{index + 1}</td>
                        <td className="font-semibold">{formatLeasingselskap(c.leasingselskap)}</td>
                        <td className="text-right">{c.total_leases.toLocaleString('nb-NO')}</td>
                        <td className="text-right font-semibold">
                          {formatDecimal(c.market_share, 2)}%
                        </td>
                        {selectedBrands.length !== 1 && (
                        <td>
                          {c.top_brands && c.top_brands.length > 0 ? (
                            <div className="space-y-1">
                              {c.top_brands.map(brand => {
                                const brandShare = (brand.count / c.total_leases) * 100
                                return (
                                  <div key={brand.brand} className="flex items-center gap-2">
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded-sm font-medium">
                                      {brand.brand}
                                    </span>
                                    <span className="text-xs text-gray-600 dark:text-gray-400">
                                      {t('leasing.companies.leases', { n: brand.count.toLocaleString('nb-NO') })}
                                    </span>
                                    <span className="text-xs font-semibold text-primary-600 dark:text-primary-400">
                                      {t('leasing.companies.shareOfCompany', { share: brandShare.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}
                                    </span>
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
